import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  CalendarIcon,
  Send,
  MessageSquare,
  Video
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { defaultView } = useParams();

  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service_interest: "",
    message: ""
  });

  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    topic: ""
  });

  const [selectedDate, setSelectedDate] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultView === "book" ? "booking" : "contact");

  useEffect(() => {
    setActiveTab(defaultView === "book" ? "booking" : "contact");
  }, [defaultView]);

  const serviceOptions = [
    { value: "staff-engagement", label: "Staff Engagement" },
    { value: "training", label: "Training" },
    { value: "health_safety", label: "Health & Safety" },
    { value: "recruitment", label: "Recruitment" },
    { value: "multiple", label: "Multiple Services" },
    { value: "not_sure", label: "Not Sure Yet" }
  ];

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
    "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"
  ];

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setContactForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        service_interest: "",
        message: ""
      });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setContactLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setBookingLoading(true);

    try {
      await axios.post(`${API}/booking`, {
        ...bookingForm,
        preferred_date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : ""
      });
      toast.success("Call booked successfully! We'll send a calendar invite shortly.");
      setBookingForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        preferred_date: "",
        preferred_time: "",
        topic: ""
      });
      setSelectedDate(null);
    } catch (error) {
      toast.error("Failed to book call. Please try again.");
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#050505]">
      <SEO
        title="Contact Us | Discuss Your Internal Communications Needs"
        description="Get in touch with The Internal Comms Agency. Book a call to discuss how we can help your organisation improve employee engagement, staff retention, onboarding and training through professional internal communications."
        keywords="contact internal comms agency, internal communications consultation, book a call, internal comms enquiry, employee engagement consultation"
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="relative py-32 md:py-40" data-testid="contact-hero">
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 opacity-30"
            style={{
              background: "radial-gradient(ellipse at bottom left, rgba(0, 229, 255, 0.15) 0%, transparent 50%)"
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-none text-white mb-6">
              LET'S TALK
            </h1>
            <p className="text-lg md:text-xl text-[#B3B3B3] max-w-2xl">
              Ready to transform your internal communications? Get in touch and 
              let's discuss how we can help unite your people and drive your culture.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <h2 className="font-display text-2xl md:text-3xl text-white mb-8">
                  GET IN TOUCH
                </h2>

                <div className="space-y-6 mb-12">
                  <a 
                    href="mailto:info@yellowagency.co.uk"
                    className="flex items-start gap-4 p-4 bg-[#141414] rounded-lg hover:bg-[#1F1F1F] transition-colors"
                    data-testid="contact-email"
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Email Us</p>
                      <p className="text-[#B3B3B3] text-sm">info@yellowagency.co.uk</p>
                    </div>
                  </a>

                  <a 
                    href="tel:+441514272900"
                    className="flex items-start gap-4 p-4 bg-[#141414] rounded-lg hover:bg-[#1F1F1F] transition-colors"
                    data-testid="contact-phone"
                  >
                    <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Call Us</p>
                      <p className="text-[#B3B3B3] text-sm">(+44) 0151 427 2900</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4 p-4 bg-[#141414] rounded-lg" data-testid="contact-location">
                    <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Location</p>
                      <p className="text-[#B3B3B3] text-sm">C/O The Yellow Agency, 2nd Floor, Consortia House, 6 Columbus Quay, Liverpool L3 4DB</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-[#141414] rounded-lg" data-testid="contact-hours">
                    <div className="w-12 h-12 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#00E5FF]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Working Hours</p>
                      <p className="text-[#B3B3B3] text-sm">Mon - Fri: 9:00 AM - 5:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Forms */}
            <div className="lg:col-span-2">
              {/* Tab Buttons */}
              <div className="flex gap-4 mb-8">
                <button
                  data-testid="tab-contact"
                  onClick={() => setActiveTab("contact")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-sm font-medium transition-colors ${
                    activeTab === "contact"
                      ? "bg-[#00E5FF] text-black"
                      : "bg-[#1F1F1F] text-white hover:bg-[#333]"
                  }`}
                >
                  <MessageSquare className="w-5 h-5" />
                  Send a Message
                </button>
                <button
                  data-testid="tab-booking"
                  onClick={() => setActiveTab("booking")}
                  className={`flex items-center gap-2 px-6 py-3 rounded-sm font-medium transition-colors ${
                    activeTab === "booking"
                      ? "bg-[#00E5FF] text-black"
                      : "bg-[#1F1F1F] text-white hover:bg-[#333]"
                  }`}
                >
                  <Video className="w-5 h-5" />
                  Book a Call
                </button>
              </div>

              {/* Contact Form */}
              {activeTab === "contact" && (
                <form 
                  onSubmit={handleContactSubmit}
                  className="p-8 bg-[#141414] rounded-lg border border-[#1F1F1F]"
                  data-testid="contact-form"
                >
                  <h3 className="font-display text-2xl text-white mb-6">
                    SEND US A MESSAGE
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="contact-name" className="text-white mb-2 block">
                        Your Name *
                      </Label>
                      <Input
                        id="contact-name"
                        data-testid="contact-name-input"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-email" className="text-white mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="contact-email"
                        data-testid="contact-email-input"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="contact-company" className="text-white mb-2 block">
                        Company Name *
                      </Label>
                      <Input
                        id="contact-company"
                        data-testid="contact-company-input"
                        required
                        value={contactForm.company}
                        onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="Your Company Ltd"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-phone" className="text-white mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="contact-phone"
                        data-testid="contact-phone-input"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="+44 123 456 7890"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="contact-service" className="text-white mb-2 block">
                      What are you interested in? *
                    </Label>
                    <Select
                      value={contactForm.service_interest}
                      onValueChange={(value) => setContactForm({ ...contactForm, service_interest: value })}
                    >
                      <SelectTrigger 
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        data-testid="contact-service-select"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1F1F1F] border-[#333]">
                        {serviceOptions.map((option) => (
                          <SelectItem 
                            key={option.value} 
                            value={option.value}
                            className="text-white hover:bg-[#333] focus:bg-[#333]"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="contact-message" className="text-white mb-2 block">
                      Your Message *
                    </Label>
                    <Textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      required
                      rows={5}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF] resize-none"
                      placeholder="Tell us about your project or challenge..."
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={contactLoading}
                    className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-3 uppercase tracking-wider"
                  >
                    {contactLoading ? (
                      <div className="spinner mr-2" />
                    ) : (
                      <Send className="w-5 h-5 mr-2" />
                    )}
                    Send Message
                  </Button>
                </form>
              )}

              {/* Booking Form */}
              {activeTab === "booking" && (
                <form 
                  onSubmit={handleBookingSubmit}
                  className="p-8 bg-[#141414] rounded-lg border border-[#1F1F1F]"
                  data-testid="booking-form"
                >
                  <h3 className="font-display text-2xl text-white mb-6">
                    BOOK A DISCOVERY CALL
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="booking-name" className="text-white mb-2 block">
                        Your Name *
                      </Label>
                      <Input
                        id="booking-name"
                        data-testid="booking-name-input"
                        required
                        value={bookingForm.name}
                        onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <Label htmlFor="booking-email" className="text-white mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="booking-email"
                        data-testid="booking-email-input"
                        type="email"
                        required
                        value={bookingForm.email}
                        onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="booking-company" className="text-white mb-2 block">
                        Company Name *
                      </Label>
                      <Input
                        id="booking-company"
                        data-testid="booking-company-input"
                        required
                        value={bookingForm.company}
                        onChange={(e) => setBookingForm({ ...bookingForm, company: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="Your Company Ltd"
                      />
                    </div>
                    <div>
                      <Label htmlFor="booking-phone" className="text-white mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="booking-phone"
                        data-testid="booking-phone-input"
                        type="tel"
                        value={bookingForm.phone}
                        onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                        className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                        placeholder="+44 123 456 7890"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label className="text-white mb-2 block">
                        Preferred Date *
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            data-testid="booking-date-picker"
                            className="w-full justify-start text-left font-normal bg-[#1F1F1F] border-[#333] text-white hover:bg-[#333]"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 text-[#00E5FF]" />
                            {selectedDate ? format(selectedDate, "PPP") : "Select a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#1F1F1F] border-[#333]">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={setSelectedDate}
                            disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                            className="bg-[#1F1F1F] text-white"
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label htmlFor="booking-time" className="text-white mb-2 block">
                        Preferred Time *
                      </Label>
                      <Select
                        value={bookingForm.preferred_time}
                        onValueChange={(value) => setBookingForm({ ...bookingForm, preferred_time: value })}
                      >
                        <SelectTrigger 
                          className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF]"
                          data-testid="booking-time-select"
                        >
                          <SelectValue placeholder="Select a time" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1F1F1F] border-[#333]">
                          {timeSlots.map((time) => (
                            <SelectItem 
                              key={time} 
                              value={time}
                              className="text-white hover:bg-[#333] focus:bg-[#333]"
                            >
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="booking-topic" className="text-white mb-2 block">
                      What would you like to discuss? *
                    </Label>
                    <Textarea
                      id="booking-topic"
                      data-testid="booking-topic-input"
                      required
                      rows={4}
                      value={bookingForm.topic}
                      onChange={(e) => setBookingForm({ ...bookingForm, topic: e.target.value })}
                      className="bg-[#1F1F1F] border-[#333] text-white focus:border-[#00E5FF] resize-none"
                      placeholder="Brief overview of your project or challenge..."
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="booking-submit-btn"
                    disabled={bookingLoading || !selectedDate || !bookingForm.preferred_time}
                    className="bg-[#00E5FF] text-black hover:bg-[#00B8CC] font-bold px-8 py-3 uppercase tracking-wider"
                  >
                    {bookingLoading ? (
                      <div className="spinner mr-2" />
                    ) : (
                      <Video className="w-5 h-5 mr-2" />
                    )}
                    Book Your Call
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
